import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, FileText, FolderKanban, Images, Lock, LogOut, Menu, MessageSquare, Unlock, UserRound, X, Newspaper } from 'lucide-react';
import { signInAnonymously } from 'firebase/auth';
import { auth } from '../firebase';
import logoHorizontal from '../assets/logo-horizontal.svg';
import logoSymbol from '../assets/logo-simbolo.svg';
import { useAlerts } from './AlertSystem';
import { APP_TRANSLATIONS } from '../constants/translations';
import Tooltip from './Tooltip';
import { ADMIN_MAX_WIDTH } from './AdminShell';
import { subscribeUnreadFeedbackCount } from '../feedbackData';
import { isInternalDevice, trackSocialClick } from '../analytics';
import { aboutHref, blogHref, homeHref } from '../siteLocale';
import { SOCIAL_LINKS } from '../constants/socialLinks';
import { DiscordIcon, InstagramIcon, TikTokIcon, TwitchIcon, YouTubeIcon } from './SocialIcons';

const MENU_SOCIALS = [
  { key: 'tiktok', label: 'TikTok', Icon: TikTokIcon },
  { key: 'youtube', label: 'YouTube', Icon: YouTubeIcon },
  { key: 'instagram', label: 'Instagram', Icon: InstagramIcon },
  { key: 'discord', label: 'Discord', Icon: DiscordIcon },
  { key: 'twitch', label: 'Twitch', Icon: TwitchIcon },
];

const NAV_ITEMS = [
  { href: homeHref, labelKey: 'navTeamUps', exact: true },
  { href: blogHref, labelKey: 'navBlog' },
];

const ADMIN_NAV_ITEMS = [
  { to: '/admin/articles', label: 'Articles', icon: FileText },
  { to: '/admin/categories', label: 'Categories', icon: FolderKanban },
  { to: '/admin/authors', label: 'Authors', icon: UserRound },
  { to: '/admin/feedback', label: 'Feedback', icon: MessageSquare },
  { to: '/admin/news', label: 'News', icon: Newspaper },
  { to: '/admin/images', label: 'Images', icon: Images },
];

// Language codes, not flags: Windows draws flag emojis as two letters ("US"),
// and a language isn't one country.
const LANGUAGES = [
  { code: 'es', label: 'Español', short: 'ES' },
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'pt', label: 'Português', short: 'PT' },
];

// Shared with AdminAccessPanel.jsx so the remembered email is consistent
// regardless of which login form the admin happens to use.
const LAST_EMAIL_STORAGE_KEY = 'rivals-admin-last-email';

export default function SiteHeader({ session, lang, onLanguageChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const { pushAlert } = useAlerts();
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const headerRef = useRef(null);

  // The phone menu closes like any menu: Esc, or a tap outside the header.
  useEffect(() => {
    if (!showMobileMenu) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') setShowMobileMenu(false);
    };
    const handlePointer = (event) => {
      if (!headerRef.current?.contains(event.target)) setShowMobileMenu(false);
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('pointerdown', handlePointer);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('pointerdown', handlePointer);
    };
  }, [showMobileMenu]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [unreadFeedbackCount, setUnreadFeedbackCount] = useState(0);

  useEffect(() => {
    if (!session.isAdmin) {
      setUnreadFeedbackCount(0);
      return undefined;
    }

    return subscribeUnreadFeedbackCount(setUnreadFeedbackCount);
  }, [session.isAdmin]);

  useEffect(() => {
    try {
      const savedEmail = window.localStorage.getItem(LAST_EMAIL_STORAGE_KEY);
      if (savedEmail) setAdminEmail(savedEmail);
    } catch {
      // localStorage unavailable - field just stays empty.
    }
  }, []);

  const currentLangObj = useMemo(
    () => LANGUAGES.find((item) => item.code === lang) || LANGUAGES[1],
    [lang]
  );

  const getNavHref = (item) => item.href(lang);
  // Compared without a trailing slash, so /es/ marks "Team ups" like /es does.
  const isNavActive = (item) => {
    const path = location.pathname.replace(/\/+$/, '') || '/';
    const href = getNavHref(item).replace(/\/+$/, '') || '/';
    return item.exact ? path === href : path === href || path.startsWith(`${href}/`);
  };
  // The public never sees the admin sign-in; Fer's devices (and an open
  // admin session) do. /admin/articles still shows the login anywhere.
  const showAdminEntry = session.isAdmin || isInternalDevice();
  const getNavLabel = (item) => APP_TRANSLATIONS[lang]?.[item.labelKey] || APP_TRANSLATIONS.en[item.labelKey];
  const lockLabelKey = session.isAdmin ? 'adminMenuTooltip' : 'adminSignInTooltip';
  const lockLabel = APP_TRANSLATIONS[lang]?.[lockLabelKey] || APP_TRANSLATIONS.en[lockLabelKey];

  const handleLanguageChange = (nextLang) => {
    setShowLanguageMenu(false);
    onLanguageChange(nextLang);
  };

  const handleLanguageToggle = () => {
    setShowAdminMenu(false);
    setShowMobileMenu(false);
    setShowLanguageMenu((value) => !value);
  };

  const handleLockClick = () => {
    if (!session.isAdmin) {
      setShowAdminMenu(false);
      setShowLoginModal(true);
      return;
    }

    setShowLanguageMenu(false);
    setShowMobileMenu(false);
    setShowAdminMenu((value) => !value);
  };

  const handleMobileMenuToggle = () => {
    setShowLanguageMenu(false);
    setShowAdminMenu(false);
    setShowMobileMenu((value) => !value);
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setLoginSubmitting(true);

    try {
      if (auth && !auth.currentUser) {
        await signInAnonymously(auth);
      }

      await session.login(adminEmail.trim(), adminPassword, rememberDevice);
      try {
        window.localStorage.setItem(LAST_EMAIL_STORAGE_KEY, adminEmail.trim());
      } catch {
        // localStorage unavailable - autocomplete convenience only, safe to skip.
      }
      setShowLoginModal(false);
      setAdminPassword('');
      pushAlert({ type: 'success', title: 'Admin login', message: 'Admin session started successfully.' });
    } catch (error) {
      pushAlert({
        type: 'error',
        title: 'Admin login',
        message: error?.message === 'not authorized'
          ? 'This account is not authorized to manage content.'
          : 'Incorrect email or password.',
      });
    } finally {
      setLoginSubmitting(false);
    }
  };

  const handleLogout = async () => {
    setShowAdminMenu(false);
    setShowLoginModal(false);
    await session.logout();
    navigate('/');
  };

  const loginModal = showLoginModal && !session.isAdmin
    ? createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 px-4 py-6 backdrop-blur-sm" onClick={() => setShowLoginModal(false)}>
          <div
            className="w-full max-w-sm rounded-2xl border border-white/10 bg-raised p-6 shadow-2xl animate-in fade-in zoom-in duration-200"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-6 flex items-center justify-between gap-4">
              <h2 className="flex items-center gap-2 text-xl font-bold text-white"><Lock size={24} className="text-cyan-300"/> Admin Login</h2>
              <button onClick={() => setShowLoginModal(false)} className="text-muted hover:text-white" aria-label="Close login modal">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleLoginSubmit}>
              <div className="mb-4">
                <label className="mb-2 block text-sm font-bold text-muted">Email</label>
                <input type="email" name="email" id="header-login-email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} className="w-full rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" placeholder="admin@example.com" autoComplete="username" autoFocus />
              </div>
              <div className="mb-4">
                <label className="mb-2 block text-sm font-bold text-muted">Password</label>
                <input type="password" name="password" id="header-login-password" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} className="w-full rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-white focus:border-cyan-400 focus:outline-none" autoComplete="current-password" />
              </div>
              <label className="mb-4 flex items-center gap-2 text-sm text-muted">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="h-4 w-4 rounded border-white/20 bg-white/5 text-cyan-400 accent-cyan-400 focus:ring-cyan-400/50"
                />
                Remember this device
              </label>
              <button type="submit" className="w-full rounded-lg bg-cyan-500 px-4 py-2 font-bold text-black hover:bg-cyan-400 transition-colors" disabled={loginSubmitting}>
                {loginSubmitting ? 'Logging in...' : 'Login'}
              </button>
            </form>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    // Named so it stays its own layer during the hero grid's view transitions -
    // otherwise the animating hero cards are painted on top of the sticky header.
    // Same color as the page, slightly see-through and blurred, so what scrolls
    // under it shows as a soft blur instead of being cut off at a hard line.
    // (The login modal is portaled to <body>: the blur would otherwise trap its
    // position: fixed inside the header.)
    <nav ref={headerRef} className="sticky top-0 z-50 bg-night/80 backdrop-blur-md border-b border-white/10 w-full shadow-lg" style={{ viewTransitionName: 'site-header' }}>
      {/* One row on every screen: the two sections sit next to the logo (the
          wordmark joins them from sm up), so phones don't lose a second
          sticky row to navigation. */}
      <div className={`${isAdminRoute ? ADMIN_MAX_WIDTH : 'max-w-7xl'} mx-auto px-4 md:px-8 h-14 md:h-16 flex items-center justify-between gap-3`}>
        <div className="flex items-center gap-3 md:gap-6 min-w-0">
          <Link to={homeHref(lang)} aria-label="Rivals Base" className="flex min-h-11 items-center gap-3 cursor-pointer shrink-0 max-sm:-mx-[5px] max-sm:px-[5px]">
            {/* The flag alone on phones (the nav needs the room), the full
                lockup from sm up. Both already carry the brand colours. */}
            <img src={logoSymbol} alt="" width="34" height="34" className="h-[34px] w-auto sm:hidden" />
            <img src={logoHorizontal} alt="" width="114" height="40" className="hidden h-9 w-auto sm:block md:h-10" />
          </Link>

          <nav className="flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.labelKey}
                to={getNavHref(item)}
                end={item.exact}
                aria-current={isNavActive(item) ? 'page' : undefined}
                className={() => `tap-44 whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-semibold transition-colors max-[359px]:px-2 md:px-4 md:py-2 ${isNavActive(item) ? 'bg-cyan-500/20 text-cyan-300' : 'text-ink hover:bg-white/10 hover:text-white'}`}
              >
                {getNavLabel(item)}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2 md:gap-4 relative">
          <div className="relative">
            <button
              onClick={handleLanguageToggle}
              className="tap-44 flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white px-3 py-2 rounded-lg transition-colors text-sm font-medium"
            >
              <span className="font-bold tracking-wide">{currentLangObj.short}</span>
              <span className="hidden md:block">{currentLangObj.label}</span>
              <ChevronDown size={16} className={`text-muted transition-transform max-[359px]:hidden ${showLanguageMenu ? 'rotate-180' : ''}`} />
            </button>

            {showLanguageMenu && (
              <div className="absolute right-0 mt-2 w-40 bg-raised border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
                {LANGUAGES.map((item) => (
                  <button
                    key={item.code}
                    onClick={() => handleLanguageChange(item.code)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors ${lang === item.code ? 'bg-cyan-500/20 text-cyan-300' : 'text-ink'}`}
                  >
                    <span className="w-6 text-xs font-bold tracking-wide text-muted">{item.short}</span>
                    <span className="font-medium text-sm">{item.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {showAdminEntry && <div className="w-px h-6 bg-white/10 mx-1 hidden md:block"></div>}

          {showAdminEntry && (
          <div className="relative hidden md:block">
            <Tooltip label={lockLabel} placement="bottom-end">
            <button
              onClick={handleLockClick}
              aria-label={lockLabel}
              className={`relative p-2 rounded-lg border transition-colors flex items-center justify-center ${session.isAdmin ? 'bg-green-500/20 border-green-500/50 text-green-400' : 'bg-white/5 border-white/10 text-muted hover:text-white hover:bg-white/10'}`}
            >
              {session.isAdmin ? <Unlock size={20} /> : <Lock size={20} />}
              {session.isAdmin && unreadFeedbackCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-cyan-400 px-1 text-[10px] font-bold text-black">
                  {unreadFeedbackCount > 9 ? '9+' : unreadFeedbackCount}
                </span>
              )}
            </button>
            </Tooltip>

            {session.isAdmin && showAdminMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-raised border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
                <Link
                  to="/admin/articles"
                  onClick={() => setShowAdminMenu(false)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors text-ink"
                >
                  <span>Articles CMS</span>
                </Link>
                <Link
                  to="/admin/categories"
                  onClick={() => setShowAdminMenu(false)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors text-ink"
                >
                  <span>Categories CMS</span>
                </Link>
                <Link
                  to="/admin/authors"
                  onClick={() => setShowAdminMenu(false)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors text-ink"
                >
                  <span>Authors</span>
                </Link>
                <Link
                  to="/admin/feedback"
                  onClick={() => setShowAdminMenu(false)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors text-ink"
                >
                  <span>Feedback</span>
                  {unreadFeedbackCount > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-cyan-400 px-1.5 text-xs font-bold text-black">
                      {unreadFeedbackCount > 99 ? '99+' : unreadFeedbackCount}
                    </span>
                  )}
                </Link>
                <Link
                  to="/admin/news"
                  onClick={() => setShowAdminMenu(false)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors text-ink"
                >
                  <span>News</span>
                </Link>
                <Link
                  to="/admin/images"
                  onClick={() => setShowAdminMenu(false)}
                  className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors text-ink"
                >
                  <span>Images</span>
                </Link>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full px-4 py-3 flex items-center gap-2 text-left hover:bg-red-500/10 transition-colors text-red-300"
                >
                  <LogOut size={16} />
                  <span>Log out</span>
                </button>
              </div>
            )}
          </div>
          )}

          {showAdminEntry && (
          <button
            type="button"
            onClick={handleMobileMenuToggle}
            className="tap-44 md:hidden relative p-2 rounded-lg border border-white/10 bg-white/5 text-ink hover:text-white hover:bg-white/10 transition-colors"
            aria-label={showMobileMenu ? 'Close menu' : 'Open menu'}
            aria-expanded={showMobileMenu}
          >
            {showMobileMenu ? <X size={20} /> : <Menu size={20} />}
            {session.isAdmin && (
              unreadFeedbackCount > 0 ? (
                <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-cyan-400 px-1 text-[10px] font-bold text-black">
                  {unreadFeedbackCount > 9 ? '9+' : unreadFeedbackCount}
                </span>
              ) : (
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-green-400 ring-2 ring-night" />
              )
            )}
          </button>
          )}
        </div>
      </div>

      {session.isAdmin && isAdminRoute ? (
        /* Second navbar line for the admin CMS: Articles/Categories/Feedback,
           always visible (both mobile and desktop) while inside /admin/* so
           there's always a way to move between CMS sections or back out of
           the article editor without returning to the lock-icon menu. */
        <div className="border-t border-white/10 bg-black/20 shadow-[0_8px_16px_-4px_rgba(0,0,0,0.4)]">
          {/* One row: on a phone it scrolls sideways instead of taking a second
              row of the always-visible header. */}
          <div className={`${ADMIN_MAX_WIDTH} mx-auto px-4 md:px-8 py-1.5 md:py-2 flex items-center gap-1 md:gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}>
            {ADMIN_NAV_ITEMS.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) => `inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors md:px-4 ${isActive ? 'bg-cyan-500/20 text-cyan-300' : 'text-ink hover:bg-white/10 hover:text-white'}`}
              >
                <Icon size={16} />
                {label}
                {label === 'Feedback' && unreadFeedbackCount > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-cyan-400 px-1.5 text-xs font-bold text-black">
                    {unreadFeedbackCount > 99 ? '99+' : unreadFeedbackCount}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      ) : null}

      {loginModal}

      {showAdminEntry && showMobileMenu && (
        <div className="md:hidden border-t border-white/10 bg-night px-4 py-3 space-y-1">
          <Link
            to={aboutHref(lang)}
            onClick={() => setShowMobileMenu(false)}
            className="block rounded-lg px-4 py-3 text-sm font-semibold text-ink transition-colors hover:bg-white/10 hover:text-white"
          >
            {APP_TRANSLATIONS[lang]?.navAbout || APP_TRANSLATIONS.en.navAbout}
          </Link>
          <div className="flex items-center gap-1 px-2" aria-label="Social media links">
            {MENU_SOCIALS.map(({ key, label, Icon }) => (
              <a
                key={key}
                href={SOCIAL_LINKS[key]}
                onClick={() => {
                  trackSocialClick(key, 'phone_menu');
                  setShowMobileMenu(false);
                }}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="flex h-11 w-11 items-center justify-center rounded-full text-ink transition-colors hover:bg-white/10 hover:text-white"
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
          <div className="my-2 border-t border-white/10" />
          {session.isAdmin ? (
            <>
              <Link
                to="/admin/articles"
                onClick={() => setShowMobileMenu(false)}
                className="block rounded-lg px-4 py-3 text-sm font-semibold text-ink transition-colors hover:bg-white/10 hover:text-white"
              >
                Articles CMS
              </Link>
              <Link
                to="/admin/categories"
                onClick={() => setShowMobileMenu(false)}
                className="block rounded-lg px-4 py-3 text-sm font-semibold text-ink transition-colors hover:bg-white/10 hover:text-white"
              >
                Categories CMS
              </Link>
              <Link
                to="/admin/authors"
                onClick={() => setShowMobileMenu(false)}
                className="block rounded-lg px-4 py-3 text-sm font-semibold text-ink transition-colors hover:bg-white/10 hover:text-white"
              >
                Authors
              </Link>
              <Link
                to="/admin/feedback"
                onClick={() => setShowMobileMenu(false)}
                className="flex items-center justify-between rounded-lg px-4 py-3 text-sm font-semibold text-ink transition-colors hover:bg-white/10 hover:text-white"
              >
                <span>Feedback</span>
                {unreadFeedbackCount > 0 && (
                  <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-cyan-400 px-1.5 text-xs font-bold text-black">
                    {unreadFeedbackCount > 99 ? '99+' : unreadFeedbackCount}
                  </span>
                )}
              </Link>
              <Link
                to="/admin/news"
                onClick={() => setShowMobileMenu(false)}
                className="block rounded-lg px-4 py-3 text-sm font-semibold text-ink transition-colors hover:bg-white/10 hover:text-white"
              >
                News
              </Link>
              <Link
                to="/admin/images"
                onClick={() => setShowMobileMenu(false)}
                className="block rounded-lg px-4 py-3 text-sm font-semibold text-ink transition-colors hover:bg-white/10 hover:text-white"
              >
                Images
              </Link>
              <button
                type="button"
                onClick={handleLogout}
                className="flex w-full items-center gap-2 rounded-lg px-4 py-3 text-left text-sm font-semibold text-red-300 transition-colors hover:bg-red-500/10"
              >
                <LogOut size={16} />
                <span>Log out</span>
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                setShowMobileMenu(false);
                setShowLoginModal(true);
              }}
              className="flex w-full items-center gap-2 rounded-lg px-4 py-3 text-left text-sm font-semibold text-ink transition-colors hover:bg-white/10 hover:text-white"
            >
              <Lock size={16} />
              <span>Admin sign in</span>
            </button>
          )}
        </div>
      )}
    </nav>
  );
}
